/* ============================================================
 * <lion-sticky-atc> — mobile sticky add-to-cart bar
 * Shows when the main add-to-cart button scrolls out of view.
 * Clicking the sticky button triggers the hidden form.
 * ============================================================ */

class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.mainBtn = document.querySelector(`#product-form-${this.sectionId} [name="add"]`);
    this.stickyBtn = this.querySelector('[data-sticky-atc-submit]');
    this.variantSelect = this.querySelector('[data-sticky-atc-variant-select]');
    if (!this.mainBtn) return;

    // Bidirectional sync with the main variant picker.
    // - User picks in sticky select → check the matching radios and dispatch
    //   change on the picker so its existing onChange handler runs the full
    //   refresh (URL, hidden id, price, gallery, etc.) — single source of truth.
    // - Page-level variant change publishes via PUB_SUB_EVENTS.variantChange,
    //   so we listen and mirror the selected option here.
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.onVariantSelectChange.bind(this));
      this.variantChangeUnsub = window.subscribe?.(
        window.PUB_SUB_EVENTS?.variantChange,
        this.onPageVariantChange.bind(this)
      );
    }

    // Reflect the main button's disabled state (sold-out, unavailable
    // variant, etc.) on the sticky button — otherwise users could tap
    // the sticky CTA and get no feedback because mainBtn.click() is a no-op
    // on disabled buttons.
    this.syncDisabledState();
    this.mainBtnObserver = new MutationObserver(this.syncDisabledState.bind(this));
    this.mainBtnObserver.observe(this.mainBtn, { attributes: true, attributeFilter: ['disabled'] });

    // Mirror the main button label so e.g. "Pre-order" or "Sold out" reaches
    // the sticky bar instead of the static "Add to cart" baked in Liquid.
    this.syncLabel();
    this.labelObserver = new MutationObserver(this.syncLabel.bind(this));
    this.labelObserver.observe(this.mainBtn, { childList: true, subtree: true, characterData: true });

    this.stickyBtn?.addEventListener('click', () => {
      if (this.mainBtn.disabled) return;
      this.mainBtn.click();
    });

    // rootMargin gives a comfortable buffer above/below so the sticky bar
    // doesn't flicker when the main ATC sits right on the viewport edge.
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.hide();
      else this.show();
    }, { threshold: 0, rootMargin: '-40px 0px -40px 0px' });

    this.observer.observe(this.mainBtn);

    this.resizeObserver = new ResizeObserver(() => this.syncViewportOffset());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mainBtnObserver?.disconnect();
    this.labelObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.variantChangeUnsub?.();
    document.documentElement.style.removeProperty('--lion-sticky-atc-offset');
  }

  onVariantSelectChange(e) {
    const variantId = e.target.value;
    const picker = document.querySelector(`lion-variant-picker[data-section="${this.sectionId}"]`);
    if (!picker) return;
    const variantsJsonEl = picker.querySelector('[data-variants-json]');
    if (!variantsJsonEl) return;
    const variants = JSON.parse(variantsJsonEl.textContent);
    const variant = variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    // Reflect this variant's option values onto the picker, whether each option
    // is rendered as radios or as a <select>.
    picker.querySelectorAll('fieldset').forEach((fs, i) => {
      const value = variant.options[i];
      const select = fs.querySelector('[data-option-value-select]');
      if (select) {
        if (select.value !== value) select.value = value;
        return;
      }
      const radio = fs.querySelector(`[data-option-value][value="${CSS.escape(value)}"]`);
      if (radio && !radio.checked) radio.checked = true;
    });
    // One bubbling change is enough — the picker's listener handles the rest.
    picker.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onPageVariantChange({ variant }) {
    if (!this.variantSelect || !variant) return;
    if (this.variantSelect.value !== String(variant.id)) {
      this.variantSelect.value = String(variant.id);
    }
  }

  syncDisabledState() {
    if (!this.stickyBtn || !this.mainBtn) return;
    this.stickyBtn.disabled = this.mainBtn.disabled;
  }

  syncLabel() {
    if (!this.stickyBtn || !this.mainBtn) return;
    // Take the inner text of the visible <span> if present, otherwise
    // the whole button text. Some themes wrap the label in a span for
    // loading-spinner overlays.
    const label = (this.mainBtn.querySelector('[data-product-form-submit-label]')?.textContent
      || this.mainBtn.textContent || '').trim();
    if (label) this.stickyBtn.textContent = label;
  }

  show() {
    this.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      this.syncViewportOffset();
    });
  }
  hide() {
    this.removeAttribute('open');
    setTimeout(() => {
      this.setAttribute('hidden', '');
      this.syncViewportOffset();
    }, 240);
  }

  syncViewportOffset() {
    const root = document.documentElement;
    if (this.hasAttribute('open') && !this.hasAttribute('hidden')) {
      root.style.setProperty('--lion-sticky-atc-offset', `${this.offsetHeight}px`);
    } else {
      root.style.removeProperty('--lion-sticky-atc-offset');
    }
  }
}

if (!customElements.get('lion-sticky-atc')) {
  customElements.define('lion-sticky-atc', StickyAtc);
}
